import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '@/api/axios';
import { ChartBar, Trophy, CurrencyDollar } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';

export const MetricasSemanal = () => {
  const { user, isAdmin } = useAuth();
  const puedeEditar = isAdmin || ['supervisor', 'community_manager'].includes(user?.role);
  const [abierto, setAbierto] = useState(false);
  const [editando, setEditando] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (abierto && !data) {
      api.get('/landing/metricas/semanal').then((r) => setData(r.data)).catch(() => {});
    }
  }, [abierto, data]);

  const guardarGasto = async (producto, semanaInicio, valor, si, wi) => {
    const monto = parseFloat(valor || '0') || 0;
    try {
      await api.put('/landing/ad-spend', { producto, semana_inicio: semanaInicio, monto });
      setData((d) => {
        const nd = { ...d, series: d.series.map((s, i) => {
          if (i !== si) return s;
          const gastos = [...s.gastos];
          gastos[wi] = monto;
          return { ...s, gastos, total_gasto: Math.round(gastos.reduce((a, b) => a + b, 0) * 100) / 100 };
        }) };
        return nd;
      });
      toast.success('Gasto guardado');
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); }
  };

  const cpl = (gasto, leads) => (gasto > 0 && leads > 0 ? `Q${(gasto / leads).toFixed(2)}` : gasto > 0 ? 'sin leads' : null);
  const max = data ? Math.max(1, ...data.series.flatMap((s) => s.leads)) : 1;

  return (
    <div className="bg-black/[0.02] border border-current/10 rounded-2xl mt-6" data-testid="metricas-semanal">
      <button onClick={() => setAbierto(!abierto)} data-testid="metricas-semanal-toggle"
        className="w-full flex items-center justify-between px-5 py-4 font-black text-sm hover:bg-black/5 transition-colors rounded-2xl">
        <span className="flex items-center gap-2"><ChartBar size={18} className="text-[#1FA3E8]" /> Rendimiento semanal por landing <span className="opacity-50 font-bold text-xs hidden sm:inline">· ¿qué anuncio rinde más?</span></span>
        <span className="opacity-50 text-xs">{abierto ? 'Cerrar ▲' : 'Ver ▼'}</span>
      </button>
      {abierto && data && (
        <div className="px-5 pb-5 overflow-x-auto">
          {puedeEditar && (
            <button onClick={() => setEditando(!editando)} data-testid="ad-spend-toggle"
              className={`mb-3 flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-full border transition-colors ${editando ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/40' : 'border-current/20 opacity-70 hover:opacity-100'}`}>
              <CurrencyDollar size={14} /> {editando ? 'Listo, dejar de anotar' : 'Anotar gasto de anuncios (Q por semana)'}
            </button>
          )}
          <table className="w-full text-xs min-w-[640px]">
            <thead>
              <tr className="text-left opacity-60 font-black uppercase tracking-wide">
                <th className="py-2 pr-3">Landing</th>
                {data.semanas.map((s, i) => (
                  <th key={s.inicio} className="py-2 px-2 text-center">Sem {s.label}{i === data.semanas.length - 1 ? ' (actual)' : ''}</th>
                ))}
                <th className="py-2 pl-2 text-center">Total 6 sem</th>
              </tr>
            </thead>
            <tbody>
              {data.series.map((s, si) => (
                <tr key={s.producto} className={`border-t border-current/10 ${si === 0 && s.total_leads > 0 ? 'bg-[#1FA3E8]/10' : ''}`} data-testid={`semanal-row-${s.producto}`}>
                  <td className="py-2.5 pr-3 font-black whitespace-nowrap">
                    {si === 0 && s.total_leads > 0 && <Trophy size={14} weight="fill" className="inline text-amber-400 mr-1" />}
                    {s.nombre}
                  </td>
                  {s.leads.map((n, wi) => (
                    <td key={wi} className="py-2.5 px-2 text-center align-top">
                      <div className="mx-auto w-8 rounded bg-[#1FA3E8]/20 relative" style={{ height: 26 }}>
                        <div className="absolute bottom-0 left-0 right-0 rounded bg-[#1FA3E8]" style={{ height: `${Math.round((n / max) * 100)}%` }} />
                        <span className="absolute inset-0 flex items-center justify-center font-black">{n}</span>
                      </div>
                      <p className="opacity-40 mt-0.5">{s.visitas[wi]} vis.</p>
                      {editando ? (
                        <input type="number" min="0" step="1" defaultValue={s.gastos[wi] || ''} placeholder="Q0"
                          data-testid={`gasto-${s.producto}-${wi}`}
                          onBlur={(e) => guardarGasto(s.producto, data.semanas[wi].inicio, e.target.value, si, wi)}
                          className="mt-1 w-16 bg-black/10 border border-current/20 rounded px-1 py-0.5 text-center text-[11px] outline-none focus:border-emerald-500" />
                      ) : (
                        s.gastos[wi] > 0 && (
                          <p className="mt-0.5 font-bold text-emerald-500 text-[10px]">Q{s.gastos[wi]}{cpl(s.gastos[wi], n) ? ` · CPL ${cpl(s.gastos[wi], n)}` : ''}</p>
                        )
                      )}
                    </td>
                  ))}
                  <td className="py-2.5 pl-2 text-center font-black text-sm">
                    {s.total_leads}
                    <p className="opacity-40 font-normal text-[10px]">{s.total_visitas} visitas</p>
                    {s.total_gasto > 0 && (
                      <p className="font-black text-emerald-500 text-[10px]" data-testid={`cpl-total-${s.producto}`}>
                        Q{s.total_gasto}{s.total_leads > 0 ? ` · CPL Q${(s.total_gasto / s.total_leads).toFixed(2)}` : ''}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="opacity-50 text-[11px] mt-3">Leads captados por el formulario de cada landing por semana (lunes a domingo). Anota el gasto semanal de tus anuncios de Facebook y el sistema calcula el <b>CPL (costo por lead)</b> = gasto ÷ leads de esa semana.</p>
        </div>
      )}
    </div>
  );
};
